/**
 * Actions para Chatbot Wizard v2
 * Sistema de navegación guiado por JSON con state machine
 */

// ========== Action Types ==========

// Control básico
export const CHATBOT_TOGGLE = 'CHATBOT_TOGGLE';
export const CHATBOT_SET_TYPING = 'CHATBOT_SET_TYPING';
export const CHATBOT_CLEAR_HISTORY = 'CHATBOT_CLEAR_HISTORY';
export const CHATBOT_LOAD_HISTORY = 'CHATBOT_LOAD_HISTORY';

// Navegación
export const CHATBOT_NAVIGATE = 'CHATBOT_NAVIGATE';
export const CHATBOT_SET_STEP = 'CHATBOT_SET_STEP';
export const CHATBOT_GO_BACK = 'CHATBOT_GO_BACK';

// Mensajes
export const CHATBOT_ADD_MESSAGE = 'CHATBOT_ADD_MESSAGE';
export const CHATBOT_ADD_USER_MESSAGE = 'CHATBOT_ADD_USER_MESSAGE';
export const CHATBOT_ADD_BOT_MESSAGE = 'CHATBOT_ADD_BOT_MESSAGE';

// Feedback y contacto
export const CHATBOT_SET_FEEDBACK = 'CHATBOT_SET_FEEDBACK';
export const CHATBOT_SUBMIT_CONTACT = 'CHATBOT_SUBMIT_CONTACT';
export const CHATBOT_UPDATE_CONTACT_FORM = 'CHATBOT_UPDATE_CONTACT_FORM';

// Contact API async actions
export const CHATBOT_CONTACT_REQUEST = 'CHATBOT_CONTACT_REQUEST';
export const CHATBOT_CONTACT_SUCCESS = 'CHATBOT_CONTACT_SUCCESS';
export const CHATBOT_CONTACT_FAILURE = 'CHATBOT_CONTACT_FAILURE';

// Timer de confirmación
export const CHATBOT_SET_TIMER = 'CHATBOT_SET_TIMER';
export const CHATBOT_TICK_TIMER = 'CHATBOT_TICK_TIMER';

// ========== Action Creators ==========

export const chatbot = {
  // Control básico
  toggle: (isOpen) => ({
    type: CHATBOT_TOGGLE,
    payload: { isOpen },
  }),

  setTyping: (isTyping) => ({
    type: CHATBOT_SET_TYPING,
    payload: { isTyping },
  }),

  clearHistory: () => {
    // También limpiar localStorage para evitar estado corrupto
    try {
      localStorage.removeItem('nawaiam_chat_history');
    } catch (e) {
      console.warn('No se pudo limpiar localStorage:', e);
    }
    return {
      type: CHATBOT_CLEAR_HISTORY,
    };
  },

  loadHistory: (data) => ({
    type: CHATBOT_LOAD_HISTORY,
    payload: data,
  }),

  // Navegación
  navigate: (step, section = null, question = null) => ({
    type: CHATBOT_NAVIGATE,
    payload: { step, section, question },
  }),

  setStep: (step) => ({
    type: CHATBOT_SET_STEP,
    payload: { step },
  }),

  goBack: () => ({
    type: CHATBOT_GO_BACK,
  }),

  // Mensajes
  addMessage: (message) => ({
    type: CHATBOT_ADD_MESSAGE,
    payload: { message },
  }),

  addUserMessage: (ref, optionLabel = null) => ({
    type: CHATBOT_ADD_USER_MESSAGE,
    payload: {
      ref,
      optionLabel,
      id: Date.now(),
      timestamp: new Date().toISOString(),
    },
  }),

  addBotMessage: (ref) => ({
    type: CHATBOT_ADD_BOT_MESSAGE,
    payload: {
      ref,
      id: Date.now() + 1,
      timestamp: new Date().toISOString(),
    },
  }),

  // Selección de opciones (navegación)
  // `fromMenu` marca que la transición nace de un clic en el menú, no de "volver"
  // ni de "más preguntas". El middleware lo usa para decidir si redirige la app.
  selectMenuOption: (sectionId, label) => ({
    type: CHATBOT_NAVIGATE,
    payload: {
      step: 'section',
      section: sectionId,
      fromMenu: true,
      userMessage: {
        ref: {
          step: 'menu',
          section: sectionId,
        },
        optionLabel: label,
      },
    },
  }),

  // `fromQuestion` cumple el mismo rol que `fromMenu`: distingue el clic real
  // sobre una pregunta de los botones de navegación interna del chat.
  selectQuestion: (questionId, label) => ({
    type: CHATBOT_NAVIGATE,
    payload: {
      step: 'answer',
      keepSection: true,
      question: questionId,
      fromQuestion: true,
      userMessage: {
        ref: {
          step: 'question',
          question: questionId,
        },
        optionLabel: label,
      },
    },
  }),

  // Feedback
  setFeedback: (helpful) => ({
    type: CHATBOT_SET_FEEDBACK,
    payload: { helpful },
  }),

  // Contacto
  updateContactForm: (field, value) => ({
    type: CHATBOT_UPDATE_CONTACT_FORM,
    payload: { field, value },
  }),

  submitContact: (email, reason) => ({
    type: CHATBOT_SUBMIT_CONTACT,
    payload: {
      email,
      reason,
    },
  }),

  // Contact API async actions
  contactRequest: () => ({ type: CHATBOT_CONTACT_REQUEST }),
  contactSuccess: () => ({ type: CHATBOT_CONTACT_SUCCESS }),
  contactFailure: (error) => ({ type: CHATBOT_CONTACT_FAILURE, payload: { error } }),

  // Timer
  setTimer: (seconds) => ({
    type: CHATBOT_SET_TIMER,
    payload: { seconds },
  }),

  tickTimer: () => ({
    type: CHATBOT_TICK_TIMER,
  }),

  // Navegación rápida
  goToMenu: () => ({
    type: CHATBOT_NAVIGATE,
    payload: {
      step: 'menu',
      section: null,
      question: null,
    },
  }),

  goToContact: () => ({
    type: CHATBOT_NAVIGATE,
    payload: {
      step: 'contact',
      userMessage: {
        ref: {
          step: 'menu',
        },
        optionLabel: { key: 'contact' },
      },
    },
  }),

  startChat: () => {
    console.log('🟢 Action: startChat() ejecutada');
    return {
      type: CHATBOT_NAVIGATE,
      payload: {
        step: 'menu',
        userMessage: {
          ref: {
            step: 'welcome',
          },
          optionLabel: { key: 'start' },
        },
      },
    };
  },
};

// Legacy compatibility (para mantener compatibilidad con código existente)
export const CHATBOT_SELECT_OPTION = 'CHATBOT_SELECT_OPTION';
export const CHATBOT_SET_NODE = 'CHATBOT_SET_NODE';
